import { useState } from 'react';
import { Loader2, CheckCircle, XCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function StepBadge({ sync }) {
  if (sync === 'synced') return <Badge variant="default" className="text-xs bg-green-600">synced</Badge>;
  if (sync === 'failed') return <Badge variant="destructive" className="text-xs">failed</Badge>;
  return <Badge variant="secondary" className="text-xs">{sync || 'unknown'}</Badge>;
}

function LiveBadge({ value, trueLabel, falseLabel }) {
  if (value === null || value === undefined) return <span className="text-xs text-muted-foreground">n/a</span>;
  return value
    ? <Badge variant="default" className="text-xs bg-green-600">{trueLabel}</Badge>
    : <Badge variant="destructive" className="text-xs">{falseLabel}</Badge>;
}

const classificationLabels = {
  paid_done_link_missing: { label: 'Paid OK, link missing', variant: 'destructive' },
  paid_missing_link_done: { label: 'Paid missing, link OK', variant: 'destructive' },
  both_missing: { label: 'Both missing', variant: 'destructive' },
  both_present_mislabelled: { label: 'Both OK (mislabelled)', variant: 'secondary' },
  probe_incomplete: { label: 'Probe incomplete', variant: 'outline' },
};

function ClassificationBadge({ cls }) {
  const cfg = classificationLabels[cls];
  if (!cfg) return <span className="text-xs text-muted-foreground">{cls || 'n/a'}</span>;
  return <Badge variant={cfg.variant} className="text-xs">{cfg.label}</Badge>;
}

function PartialRow({ row }) {
  return (
    <tr className="border-b last:border-b-0">
      <td className="py-1.5 pr-3 text-sm font-mono">
        {row.live_invoice_number || row.pennylane_invoice_id}
      </td>
      <td className="py-1.5 pr-3 text-sm font-mono">{row.pennylane_transaction_id}</td>
      <td className="py-1.5 pr-3 text-sm"><StepBadge sync={row.stored_paid_sync} /></td>
      <td className="py-1.5 pr-3 text-sm"><StepBadge sync={row.stored_match_sync} /></td>
      <td className="py-1.5 pr-3 text-sm">
        <LiveBadge value={row.live_is_paid} trueLabel="paid" falseLabel="not paid" />
      </td>
      <td className="py-1.5 pr-3 text-sm">
        <LiveBadge value={row.live_has_link} trueLabel="linked" falseLabel="no link" />
      </td>
      <td className="py-1.5 pr-3 text-sm"><ClassificationBadge cls={row.classification} /></td>
      <td className="py-1.5 text-xs text-muted-foreground max-w-[300px]">
        {row.probe_error && <div className="truncate text-red-600">{row.probe_error}</div>}
        {row.stored_warnings?.map((w, i) => (
          <div key={i} className="truncate">{w}</div>
        ))}
      </td>
    </tr>
  );
}

export function AfpvPartialApplyPanel() {
  const [loading, setLoading] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [result, setResult] = useState(null);
  const [promoteResult, setPromoteResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    setPromoteResult(null);
    try {
      const { data } = await diagnosticsAPI.afpvPartialApply();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const promote = async () => {
    setPromoting(true);
    setPromoteResult(null);
    try {
      const { data } = await diagnosticsAPI.afpvPartialApplyPromote();
      setPromoteResult(data);
      if (data.promoted > 0) {
        toast.success(`${data.promoted} record(s) promoted to applied`);
      }
      // Re-run diagnostic to refresh the view
      try {
        const { data: refreshed } = await diagnosticsAPI.afpvPartialApply();
        setResult(refreshed);
      } catch { /* non-critical */ }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPromoting(false);
    }
  };

  const rows = result?.rows || [];
  const counts = result?.classification_counts || {};

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          AFPV Partial-Apply Diagnostic
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Read-only inspection of AFPV reconcile-apply records stuck at apply_partial.
          Reports which Pennylane step (mark_as_paid vs matched_transactions) failed,
          probes live Pennylane state, and cross-checks the reconciliation label.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Button onClick={run} disabled={loading || promoting} size="sm">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Run Diagnostic
          </Button>

          {result && result.partial_count > 0 && (
            <Button
              onClick={promote}
              disabled={promoting || loading}
              size="sm"
              variant="outline"
              className="border-amber-500 text-amber-700 hover:bg-amber-50"
            >
              {promoting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Re-apply partial (AFPV)
            </Button>
          )}
        </div>

        {/* Promote result */}
        {promoteResult && (
          <div className="border rounded p-3 space-y-2">
            <h3 className="text-sm font-semibold">Promote Result</h3>
            <div className="flex gap-3 text-sm">
              <span className="text-green-700 font-mono">{promoteResult.promoted} promoted</span>
              <span className="text-amber-700 font-mono">{promoteResult.still_partial} still partial</span>
              <span className="text-red-700 font-mono">{promoteResult.errors} error(s)</span>
            </div>
            {promoteResult.rows?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b text-xs text-muted-foreground">
                      <th className="py-1 pr-3">Invoice</th>
                      <th className="py-1 pr-3">Txn ID</th>
                      <th className="py-1 pr-3">New Status</th>
                      <th className="py-1">Warnings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promoteResult.rows.map((r, i) => (
                      <tr key={i} className="border-b last:border-b-0">
                        <td className="py-1.5 pr-3 font-mono">{r.pennylane_invoice_id}</td>
                        <td className="py-1.5 pr-3 font-mono">{r.pennylane_transaction_id}</td>
                        <td className="py-1.5 pr-3">
                          <Badge
                            variant={r.new_status === 'applied' || r.new_status === 'already_applied' ? 'default' : 'destructive'}
                            className={`text-xs ${r.new_status === 'applied' || r.new_status === 'already_applied' ? 'bg-green-600' : ''}`}
                          >
                            {r.new_status}
                          </Badge>
                        </td>
                        <td className="py-1.5 text-xs text-muted-foreground">
                          {r.error && <span className="text-red-600">{r.error}</span>}
                          {r.warnings?.map((w, j) => <div key={j} className="truncate">{w}</div>)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-6">
            {/* Summary */}
            <div>
              <h3 className="text-sm font-semibold mb-1">Summary</h3>
              <div className="bg-muted/50 rounded p-2 text-sm whitespace-pre-line">
                {result.summary}
              </div>
              {result.is_uniform && result.partial_count > 0 && (
                <div className="mt-2 flex items-center gap-1 text-sm">
                  {result.dominant_failure === 'both_present_mislabelled'
                    ? <CheckCircle className="h-4 w-4 text-green-600" />
                    : <XCircle className="h-4 w-4 text-red-500" />
                  }
                  <span className="font-medium">
                    Uniform: all {result.partial_count} row(s) are{' '}
                    <ClassificationBadge cls={result.dominant_failure} />
                  </span>
                </div>
              )}
            </div>

            {/* Classification counts */}
            {result.partial_count > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-1">Classification Counts</h3>
                <div className="flex gap-3 flex-wrap">
                  {Object.entries(counts).filter(([, v]) => v > 0).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-1 text-sm">
                      <ClassificationBadge cls={k} />
                      <span className="font-mono">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Per-row table */}
            {rows.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-1">Per-Row Details ({rows.length})</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b text-xs text-muted-foreground">
                        <th className="py-1 pr-3">Invoice</th>
                        <th className="py-1 pr-3">Txn ID</th>
                        <th className="py-1 pr-3">Paid (stored)</th>
                        <th className="py-1 pr-3">Link (stored)</th>
                        <th className="py-1 pr-3">Paid (live)</th>
                        <th className="py-1 pr-3">Link (live)</th>
                        <th className="py-1 pr-3">Classification</th>
                        <th className="py-1">Error / Warning</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(r => <PartialRow key={r.action_id} row={r} />)}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Transaction ID origin */}
            {result.transaction_id_origin && (
              <div>
                <h3 className="text-sm font-semibold mb-1">Transaction ID Origin</h3>
                <div className="bg-muted/50 rounded p-2 text-sm">
                  {result.transaction_id_origin}
                </div>
              </div>
            )}

            {/* Label cross-check */}
            {result.label_cross_check && (
              <div>
                <h3 className="text-sm font-semibold mb-1">Reconciliation Label Cross-Check</h3>
                <div className="bg-muted/50 rounded p-2 text-sm">
                  {result.label_cross_check.explanation}
                </div>
                {result.label_cross_check.invoices?.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {result.label_cross_check.invoices.length} local invoice(s) found;
                    all show "Partial" on the Invoices page.
                  </p>
                )}
              </div>
            )}

            {/* Recommendation */}
            {result.recommendation && (
              <div className="border-l-4 border-amber-400 pl-3 py-1">
                <p className="text-xs font-medium text-amber-700">Recommendation</p>
                <p className="text-sm">{result.recommendation}</p>
              </div>
            )}

            {/* Timestamp */}
            <p className="text-xs text-muted-foreground">
              Checked at: {result.checked_at}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
